import { Maximize2, ExternalLink } from 'lucide-react';
import CustomTooltip from '../../Tooltip/CustomTooltip';

/**
 * Container for VizCopyButton, VizDownloadButton, and optional fullscreen button.
 * Provides consistent positioning and hover-reveal behavior.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children    - VizCopyButton / VizDownloadButton instances
 * @param {string} [props.className]          - Override or extend default positioning classes
 * @param {function} [props.onFullscreen]     - When provided, shows a Maximize2 fullscreen button
 * @param {function} [props.onOpenInNebula]   - When provided, shows an "Open in Nebula" button
 */
const VizActionBar = ({
  children,
  className = "absolute top-2 right-2 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity",
  onFullscreen,
  onOpenInNebula,
  leftContent,
}) => {
  const btnClass =
    "flex items-center p-2 bg-fill-white dark:bg-fill-editor-surface border-[0.5px] dark:border-edge-editor-border border-edge-border text-ink-ink dark:text-ink-white rounded-lg hover:bg-fill-gray-100 dark:hover:bg-fill-editor-hover transition-colors";

  const fullscreenBtn = onFullscreen && (
    <CustomTooltip content="Fullscreen" side="top" align="center">
      <button className={btnClass} onClick={onFullscreen} aria-label="Open fullscreen">
        <Maximize2 className="w-3.5 h-3.5 hover:scale-110 transition-transform" />
      </button>
    </CustomTooltip>
  );

  const openInNebulaBtn = onOpenInNebula && (
    <CustomTooltip content="Open in Nebula" side="top" align="center">
      <button className={btnClass} onClick={onOpenInNebula} aria-label="Open in Nebula">
        <ExternalLink className="w-3.5 h-3.5 hover:scale-110 transition-transform" />
      </button>
    </CustomTooltip>
  );

  // Header-row mode: always visible, toggle on left, buttons on right
  if (leftContent) {
    return (
      <div className="flex items-center justify-between px-1 py-1 mb-1">
        {leftContent}
        <div className="flex items-center gap-1.5">
          {children}
          {openInNebulaBtn}
          {fullscreenBtn}
        </div>
      </div>
    );
  }

  // Legacy floating overlay mode (standalone usage without toggle)
  return (
    <div className={className}>
      {children}
      {openInNebulaBtn}
      {fullscreenBtn}
    </div>
  );
};

export default VizActionBar;
